import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation, I18nextProvider } from 'react-i18next';
import i18next from 'i18next';
import { Button } from 'react-bootstrap';
import {
  MdPhotoCamera, MdPerson, MdCheckCircle, MdMeetingRoom, MdBlock,
} from 'react-icons/md';
import axios from 'axios';
import { useStationScannerListener } from './useStationScannerListener';
import QrScannerModal from './QrScannerModal';
import liveTrainingLogo from './assets/livetraining-logo.png';
import { getContentHandler } from './components/courseContent';
import { getMaterialsFromCourseData } from './trainingMaterials';
import { DOSTEP, postStation, sprawdzDostep } from './stationAccess';
import HealthStatementCard from './components/HealthStatementCard';

// Stanowisko rejestracyjne (portiernia) — osobny "reaktor" od logowania pracownika w App.js.
// Skanuje TA SAMA plakietka co na bramie i w mailu z zaproszeniem, tym samym torem co
// `identifyByBadgeCode` w App.js (kod surowy, parsuje go backend), ale odpowiedz i zachowanie
// ekranu sa inne — patrz `RegistrationStationController::skan()` (Task 7 planu).
export const STATION_STATE = {
  IDLE: 'idle',
  POWITANIE: 'powitanie',
  GRUPA: 'grupa',
  SZKOLENIE: 'szkolenie',
  ZALICZONE: 'zaliczone',
  OSWIADCZENIE: 'oswiadczenie',
};

// Fibonacci (styl.liczba-spoza-fibonacciego) — bylo 6, wyladowywalo z konwencji liczbowej pliku.
// RUNDA 6 (Marcin, 2026-09-17): podniesione z 8 do 13 (kolejna liczba Fibonacciego) — wlasciciel
// zazadal, zeby siatka kafelkow osob byla ZMIERZONA, zweryfikowana dla 10 REALNYCH uczestnikow
// (nie deklarowana/zakladana), a poprzedni limit 8 uniemozliwialby przepuszczenie 10 osob przez
// PRAWDZIWY tor skanowania w gate'cie (dziewiata/dziesiata osoba trafialaby w `station_group_full`
// i nigdy nie dolaczylaby do listy, wiec siatka nigdy nie zostalaby naprawde przetestowana na 10
// kafelkach). 13 daje bezpieczny margines nad 10 bez zmiany jakiejkolwiek innej logiki grupy.
export const MAX_GROUP_SIZE = 13;

// Zgloszenie Dawida Blachowskiego (Marcin, 2026-09-17): "zawsze widze zarejestrowano wejscie,
// nie wiem czy wyjscie w ogole dziala". Przyczyna — WSZYSTKO, co nie bylo doslownie `'out'`,
// wpadalo pod komunikat wejscia, WLACZNIE z `duplicate` (czytnik odczytal ten sam kod dwa razy
// w kilka sekund) i `suppressed` (backend swiadomie POMINAL zapis, bo poprzednie odbicie tej
// osoby bylo przed chwila — okno tlumienia `gate:same_scan_window_minutes`, na werken169: 3 min).
// Ekran GRATULOWAL wejscia przy odczytach, ktore NIC nie zapisaly — operator nie mial jak sie
// zorientowac, ze trzeba uzyc recznej korekty w panelu ochrony. Rozlaczne komunikaty, bez
// fallbacku udajacego 'in'.
//
// RUNDA 6b (Marcin, 2026-09-17): backend (`GateScanProcessor`, commit db90ce760) dostal PRZESUWNE
// okno odbicia i nowa wartosc `still_inside` — odbicie osoby, ktora JEST juz na terenie, w oknie
// tlumienia (kotwica okna przesuwa sie, ale NOWY WPIS BRAMY NIE POWSTAJE). Wlasciciel: "jak bede
// sie odbijal co 15 sekund, to co 15 sekund pokazuje mi informacje, ze czesc, witaj w zakladzie"
// — TEN SAM EKRAN powitania co `in` (imie, firma, wazność szkolen), ale komunikat MUSI mowic
// wylacznie to, co naprawde sie stalo: `still_inside` NIE jest nowym wejsciem, wiec zdanie
// "zarejestrowano wejscie" byloby NIEPRAWDA — dokladnie ta sama klasa bledu, ktora ten plik juz
// raz naprawial. `station_greeting_still_inside` potwierdza OBECNOSC ("jestes na terenie
// zakladu"), nie zapisuje nowego zdarzenia.
// UWAGA (WK 2026-10-01): `denied` NIE jest w tej mapie — ma osobna galaz czerwonej karty z powodem
// (`DENIED_KEY_BY_REASON` ponizej, render w `POWITANIE`), a nie fallback `|| 'in'`.
export const GREETING_KEY_BY_GATE_ACTION = {
  in: 'station_greeting_in',
  out: 'station_greeting_out',
  duplicate: 'station_greeting_duplicate',
  suppressed: 'station_greeting_suppressed',
  still_inside: 'station_greeting_still_inside',
};

// Podtytul pod tytulem powitania (WK 2026-10-01) — tylko wejscie i wyjscie maja zdanie wyjasniajace.
export const GREETING_SUB_KEY_BY_GATE_ACTION = {
  in: 'station_greeting_in_sub',
  out: 'station_greeting_out_sub',
};

// Odmowa Z POWODEM (WK 2026-10-01): gosc z waznym szkoleniem nie moze czytac „dostep ODMOWIONY",
// skoro brakuje mu tylko celu wizyty — komunikat nazywa to, co sie stalo, i mowi, dokad isc.
// Powod spoza mapy (`other`, `health_statement_missing`, wartosc z przyszlej wersji backendu)
// spada na `station_denied_other` — nigdy na powitanie.
export const DENIED_KEY_BY_REASON = {
  no_permit_or_visit: 'station_denied_no_permit_or_visit',
  missing_qualifications: 'station_denied_missing_qualifications',
  account_inactive: 'station_denied_account_inactive',
  permit_deadline_exceeded: 'station_denied_permit_deadline_exceeded',
  health_statement_missing: 'station_denied_health_statement',
};
const DENIED_FALLBACK_KEY = 'station_denied_other';

// Komunikat odmowy nazywa, czego brakuje i co zrobic (Marcin 2026-10-06): nazwy uprawnien, numer i
// termin pozwolenia z `denialDetails`. Starszy backend bez szczegolow -> wariant `_generic`.
// Bez imienia tekst nie moze zaczynac sie od przecinka: zdejmujemy „, " i podnosimy pierwsza litere.
// Tag jezyka z i18n bywa nieprawidlowy (np. `pl_PL` z adresu `?lng=`) i wtedy Intl rzuca RangeError:
// probujemy jezyk, potem `pl`; `null` = zaden tag nie przeszedl (wolajacy ma wlasny ostatni fallback).
const zTagiemJezyka = (language, format) => {
  for (const tag of [language, 'pl']) {
    try {
      if (tag) return format(tag);
    } catch (blad) { /* nieprawidlowy tag jezyka — nastepna proba */ }
  }
  return null;
};

// Termin pozwolenia w strefie zakladu, nie urzadzenia: kiosk z zegarem w UTC pokazalby dzien wczesniej.
const formatDeadline = (iso, language) => {
  const date = iso ? new Date(iso) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  return zTagiemJezyka(language, (tag) => date.toLocaleDateString(tag, {
    day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Warsaw',
  })) || iso.slice(0, 10);
};

// Odmowa w jezyku OSOBY (konto), nie kiosku: instrukcja, co zrobic, ma byc zrozumiala dla niej.
export const deniedText = (tKiosku, greeting, kioskLanguage) => {
  const language = greeting.language || kioskLanguage;
  const t = greeting.language ? i18next.getFixedT(greeting.language) : tKiosku;
  const reason = greeting.denialReason;
  const details = greeting.denialDetails || {};
  const params = { firstName: greeting.firstName || '' };
  let key = DENIED_KEY_BY_REASON[reason] || DENIED_FALLBACK_KEY;
  if (reason === 'missing_qualifications') {
    const names = (Array.isArray(details.missingQualificationNames) ? details.missingQualificationNames : [])
      .filter((n) => typeof n === 'string' && n.trim() !== '');
    if (names.length) params.qualifications = names.join(', ');
    else key = 'station_denied_missing_qualifications_generic';
  }
  if (reason === 'permit_deadline_exceeded') {
    const deadline = formatDeadline(details.permitDeadline, language);
    if (details.permitId && deadline) {
      params.permitId = details.permitId;
      params.deadline = deadline;
    } else {
      key = 'station_denied_permit_deadline_exceeded_generic';
    }
  }
  const text = t(key, params);
  if (greeting.firstName) return text;
  const bezImienia = text.replace(/^\s*,\s*/, '');
  return bezImienia.charAt(0).toUpperCase() + bezImienia.slice(1);
};
// Co ile ponawiac sprawdzenie dostepu, gdy stanowisko stoi na ekranie awarii lacznosci
// (Fibonacci, CLAUDE.md). 13 s: dosc rzadko, zeby nie dobijac backendu, ktory wlasnie wstaje,
// i dosc czesto, zeby obsluga nie zdazyla uznac kiosku za zepsuty.
const PONOWIENIE_PO_AWARII_MS = 13000;

// K8 (WK 2026-10-02): karta wejscia/wyjscia ma byc czytelna z 2-3 m i widoczna dosc dlugo, zeby
// osoba idaca dalej zdazyla ja zobaczyc — 5 s (Fibonacci), wczesniej 3 s.
// Godzina na karcie. Tag jezyka z i18n bywa nieprawidlowy (np. `pl_PL` z adresu `?lng=`) i wtedy
// `toLocaleTimeString` rzuca RangeError w trakcie renderu — wejscie jest juz zapisane, wiec karta
// NIE moze przez to zniknac: spadamy na `pl`, a w ostatecznosci na HH:MM recznie.
const formatGateTime = (date, language) => {
  if (!date) return '';
  return zTagiemJezyka(language, (tag) => date.toLocaleTimeString(tag, { hour: '2-digit', minute: '2-digit' })) || `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

export const GREETING_AUTO_RETURN_MS = 5000;
// Podmiana karty (kolejny skan w trakcie karty) restartuje odliczanie, ale CALE okno POWITANIE nie
// moze przekroczyc tego limitu od pierwszej karty — inaczej strumien ludzi blokowalby kolejke
// oswiadczen zdrowotnych, drenowana dopiero na ekranie bazowym (Fibonacci).
export const GREETING_MAX_WINDOW_MS = 13000;
// Odmowa musi zostac na ekranie dosc dlugo, by ja przeczytac (Fibonacci).
const DENIED_AUTO_RETURN_MS = 8000;
// Karta oswiadczenia bez akcji wraca na ekran bazowy (Fibonacci, jak komunikaty bledow).
const STATEMENT_TIMEOUT_MS = 34000;
const COMPLETED_AUTO_RETURN_MS = 3000;
// K8b (WK 2026-10-02): gdy po zaliczeniu sa karty wejscia/odmowy, „Zaliczono szkolenie" widac
// krotko (Fibonacci), a potem kolejno karty.
const COMPLETED_BEFORE_CARDS_MS = 2000;

// 🔒 BEZPIECZENSTWO (Marcin, 2026-09-17): sekret stanowiska NIGDY nie trafia do przegladarki.
// Wczesniejsza wersja wstrzykiwala go tu jako zmienna srodowiskowa builda (osobny naglowek
// wysylany z JS) — to znaczy, ze KAZDY, kto otworzy adres stanowiska, odczytalby go ze zrodla
// strony. Zamiast tego wolamy WZGLEDNA sciezke same-origin
// (`/szkolenia/webapi/stanowisko/...`) — nginx tego kontenera (devops/docker/dockerfs) proxy'uje
// ja do backendu instalacji i SAM dokłada naglowek uwierzytelniajacy z env kontenera
// (entrypoint.sh), poza zasiegiem JS. Kodu, ktory czyta albo wysyla ten sekret, tu byc NIE MOZE
// — pilnuje tego RegistrationStation.tokenLeak.test.js.
//
// ✅ [sec.stanowisko-proxy-bez-autoryzacji] — DOMKNIETE (Marcin, 2026-09-23). Wczesniej stalo
// tu ostrzezenie, ze nginx doklada uprzywilejowany naglowek KAZDEMU, kto dosiegnie publicznego
// hosta stanowiska, wiec kazdy taki ktos mogl wolac endpointy stanowiska. Od teraz backend
// wymaga DRUGIEGO tokenu — tego z LINKU kiosku (`?t=`), rotowanego przez superadmina
// w ustawieniach systemowych. Cala jego obsluga siedzi w `stationAccess.js` (i tylko tam, zeby
// ten plik zostal wolny od napisow przypominajacych naglowek stanowiska — patrz docblock tamtego
// modulu). Bramka serwerowa stoi na KAZDYM endpoincie stanowiska, nie tylko na ekranie ponizej.

/**
 * Krotki dzwiek bez modala i bez przejecia fokusu. Web Audio API nie istnieje w jsdom (testy) ani
 * na kazdej przegladarce — brak dzwieku jest efektem UBOCZNYM, nigdy warunkiem dzialania skanu.
 */
const playTone = ({ frequency, type, duration, volume }) => {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = type;
    oscillator.frequency.value = frequency;
    gain.gain.value = volume;
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.start();
    oscillator.stop(ctx.currentTime + duration);
    oscillator.onended = () => ctx.close();
  } catch (e) {
    // Brak dzwieku nie moze wywalic skanu — patrz komentarz wyzej.
  }
};

// Sukces: 880 Hz sine (jak dotad). Gra WYLACZNIE przy skanie, ktory kogos wpuszcza.
const playScanBeep = () => playTone({ frequency: 880, type: 'sine', duration: 0.13, volume: 0.1 });

// Odmowa: 220 Hz square 0,4 s, jak `playDenyBeep()` w panelu ochrony (WorkerHeaderCard.vue) —
// nizszy, ostrzejszy ton, odroznialny od sukcesu bez patrzenia na ekran.
const playDenyBeep = () => playTone({ frequency: 220, type: 'square', duration: 0.4, volume: 0.3 });

/** Imie i nazwisko osobno (backend WK) z bezpiecznym fallbackiem na `displayName` (starszy backend). */
const personNames = (data) => {
  const display = (data.displayName || '').trim();
  const firstName = (data.firstName || display.split(/\s+/)[0] || '').trim();
  const fullName = [data.firstName, data.lastName].filter(Boolean).join(' ').trim() || display;
  return { firstName, fullName };
};

/**
 * JEDNO zrodlo prawdy o tym, czy osoba idzie na szkolenie (ekran GRUPA, dzwiek sukcesu) czy
 * dostaje odmowe (czerwona karta, dzwiek 220 Hz): licznik brakujacych szkolen ALBO powod
 * `missing_trainings`. Ekran i dzwiek NIGDY nie rozstrzygaja po roznych polach.
 */
const maBrakujaceSzkolenia = (data) => (data.missingTrainingsCount || 0) > 0
  || data.denialReason === 'missing_trainings';

/**
 * JEDEN wspolny kontener dla WSZYSTKICH komunikatow stanowiska (Marcin, 2026-09-17: "informacje
 * i komunikaty powinny wyskakiwac na srodku ekranu i najlepiej w glasmorficznym kontenerku (...)
 * wszystko centrujemy do srodka ekranu" — bez wyjatkow, KAZDY stan: spoczynek, powitanie,
 * pozegnanie, grupa, zaliczone, komunikaty bledow). Uzywany przez idle/powitanie/grupa/zaliczone —
 * NIE przez ekran "szkolenie" (odtwarzacz materialu ZOSTAJE poza szklanym kontenerem, wprost na
 * tle, decyzja wlasciciela). Jedno miejsce definiujace uklad = kolejna zmiana wygladu nie
 * rozjedzie sie miedzy ekranami (dokladnie to, co rozjechalo sie przy poprzedniej wersji, gdzie
 * kazdy stan mial wlasny zestaw klas kontenera).
 *
 * `min-vh-100 d-flex align-items-center justify-content-center` centruje KARTE w PIONIE i
 * POZIOMIE na calym ekranie — usterka znaleziona na produkcji (zrzut ekranu grupy): karta
 * przyklejona do gory, dolne 2/3 ekranu puste tlo. `.lt-stanowisko-glass-card` (index.css) daje
 * kontrast NIEZALEZNY od jasnosci zdjecia zakladu pod spodem (ciemna polprzezroczysta plama +
 * biale pismo, nie liczymy na to, ze akurat wyjdzie czytelnie na jasnym niebie).
 */
const StationGlassScreen = ({ testId, cardClassName = '', children }) => (
  <div
    className="App bg-transparent min-vh-100 d-flex align-items-center justify-content-center text-center px-3"
    data-testid={testId}
  >
    <div className={`lt-stanowisko-glass-card ${cardClassName}`.trim()}>
      {children}
    </div>
  </div>
);

// Dane karty bramy (`greeting`) z odpowiedzi `/skan`, `/oswiadczenie` albo jednego elementu
// `completed[]` z `/zalicz-grupowo` (te same pola: firstName, lastName, companyExternal, ...).
const buildGreeting = (data) => {
  const { firstName, fullName } = personNames(data);
  return {
    displayName: data.displayName,
    fullName,
    firstName,
    gateAction: data.gateAction,
    denialReason: data.denialReason || null,
    denialDetails: data.denialDetails || null,
    language: data.language || null,
    companyLogoUrl: data.companyExternal && data.companyLogoUrl ? data.companyLogoUrl : null,
  };
};

/**
 * K8b: karty po zaliczeniu grupowym. Kazda osoba, ktorej wejscie ZAPISANO, jest na karcie wejscia
 * (jedna osoba = zwykla karta K8, wiele = JEDNA karta zbiorcza z lista imion — czytelniejsza niz
 * `MAX_GROUP_SIZE` kart po 5 s). Kazda osoba, ktorej wejscia NIE zapisano mimo zaliczenia (np.
 * brak wizyty/pozwolenia firmy zewnetrznej), dostaje wlasna czerwona karte odmowy z powodem.
 * Osoba juz na terenie (`already_inside`) nie dostaje karty — nowego wejscia nie ma.
 * Starszy backend bez `gateAction`: `entryRecorded` rozstrzyga o wejsciu, a odmowa idzie na
 * powod ogolny.
 */
const zaliczenieCards = (completed) => {
  if (!Array.isArray(completed)) return [];
  const entered = completed.filter((c) => c.gateAction === 'in' || (!c.gateAction && c.entryRecorded === true));
  // `already_inside` to status werdyktu bramy PO zaliczeniu (GateScanOutcome::STATUS_ALREADY_INSIDE,
  // zwraca go wylacznie /zalicz-grupowo; /skan nigdy) — osoba juz na terenie, bez karty.
  // `statement_required` (doba zmienila sie w trakcie szkolenia): wejscia nie zapisano, a odpowiedz
  // nie niesie tresci oswiadczenia — karta mowi, ze trzeba zeskanowac kod ponownie (wtedy /skan
  // pokaze karte oswiadczenia).
  const rejected = completed
    .filter((c) => c.gateAction === 'denied' || c.gateAction === 'statement_required'
      || (!c.gateAction && c.entryRecorded === false && c.reason === 'other_requirements_missing'))
    .map((c) => (c.gateAction === 'statement_required' ? { ...c, denialReason: 'health_statement_missing' } : c));
  const cards = [];
  if (entered.length === 1) {
    cards.push({ ...buildGreeting({ ...entered[0], gateAction: 'in' }), gateAction: 'in' });
  } else if (entered.length > 1) {
    cards.push({
      gateAction: 'in',
      group: entered.map((c) => ({ key: String(c.userId ?? c.displayName), name: buildGreeting(c).fullName })),
      companyLogoUrl: null,
    });
  }
  // Odmowy tez w JEDNEJ karcie, gdy jest ich wiele: kolejka to najwyzej 2 karty (5 s + 8 s =
  // GREETING_MAX_WINDOW_MS), wiec okno POWITANIE nie blokuje kolejki oswiadczen zdrowotnych, a
  // zadna odmowa nie przepada. Powod konkretnej osoby widac po ponownym skanie.
  if (rejected.length === 1) {
    cards.push({ ...buildGreeting({ ...rejected[0], gateAction: 'denied' }), gateAction: 'denied' });
  } else if (rejected.length > 1) {
    cards.push({
      gateAction: 'denied',
      // Kazda osoba na karcie zbiorczej z wlasnym brakiem (Marcin 2026-10-06: zawsze mowic, czego brakuje).
      group: rejected.map((c) => {
        const g = buildGreeting(c);
        // Powod nierozpoznany = to samo zdanie co tytul karty: kafelek bez dopisku zamiast powtorki.
        const znany = Object.prototype.hasOwnProperty.call(DENIED_KEY_BY_REASON, g.denialReason);
        return { key: String(c.userId ?? c.displayName), name: g.fullName, denial: znany ? { ...g, firstName: '' } : null };
      }),
      companyLogoUrl: null,
    });
  }
  return cards;
};

const RegistrationStationScreens = ({ onNotice }) => {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState(STATION_STATE.IDLE);
  const [participants, setParticipants] = useState([]); // [{ userId, displayName, language, trainingId }]
  const [groupTrainingId, setGroupTrainingId] = useState(null);
  // { displayName, fullName, firstName, gateAction, denialReason, companyLogoUrl }
  const [greeting, setGreeting] = useState(null);
  // Oswiadczenie zdrowotne (BRC): { text, code, joinGroup } — brama NIE zapisala wejscia, czeka na OK.
  const [statement, setStatement] = useState(null);
  const [statementBusy, setStatementBusy] = useState(false);
  const [statementError, setStatementError] = useState(null);
  // Dokad wracamy z karty (GRUPA, gdy karta przerwala zbieranie grupy; inaczej ekran bazowy).
  const statementReturnRef = useRef(STATION_STATE.IDLE);
  const zaliczanieWTokuRef = useRef(false);
  // Kolejka FIFO osob, ktore zeskanowaly sie, gdy ekran byl zajety: { code, text, name }.
  const statementQueueRef = useRef([]);
  const statementRef = useRef(null);
  statementRef.current = statement;
  const participantsRef = useRef([]);
  participantsRef.current = participants;
  const [logoHidden, setLogoHidden] = useState(false); // logo firmy, ktore nie zaladowalo sie (onError)
  const [completedNames, setCompletedNames] = useState([]);
  // Kamera jako DRUGA awaryjna droga (Marcin, 2026-09-17 — polecenie wlasciciela na czas
  // testow): "przycisk z kamerka, ktory po wcisnieciu uruchamia kamere z laptopa (...) tylko
  // TA funkcja dziala po klikniecu, a tak modul dziala, jakby nie wymagal tej funkcji". Czytnik
  // klawiaturowy zostaje DOMYSLNA i JEDYNA aktywna sciezka — kamera (QrScannerModal, wariant
  // domyslny "dialog") montuje sie/startuje WYLACZNIE gdy `cameraOpen === true`, czyli
  // WYLACZNIE po klikniecu przycisku (`useQrScanner.start()` wewnatrz QrScannerModal odpala sie
  // dopiero w efekcie zaleznym od jego propsa `show`) — zero automatycznego pytania o dostep do
  // kamery na laptopie, ktory jej nawet nie ma.
  const [cameraOpen, setCameraOpen] = useState(false);
  // Znak zakladu klienta (Marcin, 2026-09-17: "logo Werner Kenkel ponizej logo LiveTraining, zeby
  // bylo, ze to jest widok zakladu klienta"). Instancja wystawia go publicznie pod
  // `${backendOrigin}/logo` (PUBLIC_ACCESS w security.yaml backendu, sprawdzone na werken169:
  // 200/image/png/124x100). `null` dopoki obraz sie NIE zaladuje pomyslnie — instalacja bez
  // skonfigurowanego logo klienta ma pokazywac SAM naglowek LiveTraining, zero zepsutej ikony
  // czy dziury w ukladzie (`<img>` ponizej w ogole sie NIE montuje, dopoki stan jest `null`).
  const [clientLogoUrl, setClientLogoUrl] = useState(null);
  const [plantLogoBroken, setPlantLogoBroken] = useState(false);
  // Materiał realnego szkolenia (Task 11b) — pobrany TA SAMĄ ścieżką co Training.js
  // (`GET {apiUrl}{trainingId}`, ten sam kształt odpowiedzi: `training.materials[]`/`training.pdf`),
  // odtwarzany TYM SAMYM komponentem (`getContentHandler` -> VideoContent/PDFCourseContent), zeby
  // stanowisko nigdy nie mialo drugiej kopii odtwarzacza.
  const [courseMaterials, setCourseMaterials] = useState([]);
  const [materialIndex, setMaterialIndex] = useState(0);
  const [courseShowSeekControls, setCourseShowSeekControls] = useState(undefined);
  // Widoczny dla operatora komunikat o niepowodzeniu (brak trainingId, brak materialu, blad
  // pobrania) — NIGDY cicha zaslepka. Kasowany przy kolejnym udanym skanie/starcie.
  const [stationError, setStationError] = useState(null);

  // Bramka dostepu do panelu (Marcin, 2026-09-23): `null` = jeszcze sprawdzamy, dalej jedna
  // z wartosci `DOSTEP` (ok / odmowa / awaria). Dopoki `null`, ekran nie rysuje NIC poza neutralna
  // plansza — inaczej przez ulamek sekundy migalby dzialajacy panel na urzadzeniu, ktore za
  // chwile dostanie odmowe.
  //
  // 🟠 `[access-gate-fail-open]` (kontrola MR !34): awaria backendu ma WLASNY werdykt i wlasny
  // ekran. Ani nie udaje odmowy („adres nieaktualny" wyslaloby operatora po nowy link zamiast
  // zglosic awarie), ani nie udaje dostepu (pelny panel zachecalby do skanowania ludzi
  // w stanowisko, ktore i tak nic nie zapisze).
  const [dostep, setDostep] = useState(null);

  // Blokada jak przy kamerze w QrScannerModal (`decodedRef`) — jedno fizyczne wejscie (jeden
  // skan) nie moze wyslac dwoch zadan, dopoki poprzednie sie nie rozstrzygnie.
  //
  // 🔴 KOREKTA (kontrola/wartownik, finding [skan-utracony-przy-rownoleglym-zadaniu], commit
  // f5470ac8): to byl WCZESNIEJ pojedynczy `useRef(false)` — jeden globalny bool blokowal
  // KAZDY kolejny skan, nie tylko duplikat TEGO SAMEGO kodu. Przy portierni, gdzie ludzie
  // odbijaja sie jeden za drugim, zadanie osoby A (wciaz w locie) gubilo CALKOWICIE skan osoby
  // B — jej wejscie nigdy nie trafialo do `/skan`, zero komunikatu dla operatora. Teraz
  // blokada jest PER KOD: `Set` kodow aktualnie w locie — drugi fizyczny skan INNEGO kodu
  // przechodzi rownolegle, a duplikat TEGO SAMEGO kodu (dwa szybkie odczyty tej samej
  // plakietki, zanim pierwsze zadanie sie rozstrzygnie) nadal jest blokowany, dokladnie jak
  // dotad.
  const requestsInFlightRef = useRef(new Set());
  const timerRef = useRef(null);
  // Odczyt aktualnego stanu wewnatrz obsluzonej obietnicy (`wyslijNaStanowisko('/skan', ...).then`) bez
  // funkcyjnego updatera `setState(prev => ...)` z efektami ubocznymi w srodku — ten wzorzec
  // wywolywal dodatkowy przebieg renderu poza `act()` w testach (ostrzezenie React 18).
  const stateRef = useRef(state);
  stateRef.current = state;
  const cardQueueRef = useRef([]); // K8b: karty do pokazania po zaliczeniu grupowym
  const shownQueueCardRef = useRef(null); // K8b: karta z kolejki wyswietlana TERAZ (juz zdjeta z kolejki)
  const greetingWindowStartRef = useRef(0); // poczatek okna POWITANIE (pierwsza karta)

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  // RUNDA 6 (Marcin, 2026-09-17): "logo LiveTraining powiekszyc trzykrotnie, ale tak, zeby nie
  // przekraczal w calosci szerokosci napisu «Zeskanuj kod»". Zamiast szywac limit na sztywno w
  // px, MIERZYMY realna, wyrenderowana szerokosc naglowka ekranu spoczynku (`idleTitleRef`) i
  // trzymamy ja jako gorne ograniczenie (`liveLogoMaxWidth`) — dziala niezaleznie od jezyka
  // (dluzszy/krotszy tekst w innych jezykach daje inna szerokosc) i od rozdzielczosci (font-size
  // naglowka skaluje sie `clamp()`-em). Ekran spoczynku (IDLE) jest stanem POCZATKOWYM
  // komponentu, wiec ten pomiar zawsze zdazy wykonac sie PRZED pierwszym skanem — zmierzona
  // wartosc zostaje w stanie i jest uzywana przez `renderHeader()` na WSZYSTKICH ekranach (spojny
  // rozmiar loga niezaleznie od aktualnego stanu), nie tylko na idle.
  const idleTitleRef = useRef(null);
  const [liveLogoMaxWidth, setLiveLogoMaxWidth] = useState(null);

  // 🔴 Zaleznosc od `dostep` jest KONIECZNA, nie ozdobna (regresja zlapana testem
  // „logo (...) dostaje inline maxWidth", 2026-09-23): od czasu bramki dostepu pierwszy render
  // to plansza sprawdzania, a NIE ekran spoczynku — `idleTitleRef` jest wtedy pusty, wiec pomiar
  // nie ma czego zmierzyc. `state` przez caly ten czas pozostaje `IDLE`, wiec bez tej zaleznosci
  // efekt NIGDY by sie nie powtorzyl i logo zostaloby bez ograniczenia szerokosci na KAZDYM
  // ekranie (naglowek jest wspolny) — dokladnie ten defekt, ktory RUNDA 6 naprawiala.
  useLayoutEffect(() => {
    if (state !== STATION_STATE.IDLE) return undefined;
    const measure = () => {
      if (idleTitleRef.current) {
        setLiveLogoMaxWidth(idleTitleRef.current.getBoundingClientRect().width);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [state, dostep]);

  // 🔒 Bramka dostepu — pierwsze sprawdzenie, przy montowaniu (Marcin, 2026-09-23).
  //
  // 🟠 `[komentarz-myli-czytelnika]` (kontrola MR !34): staly tu wczesniej DWA rozjechane opisy.
  // Akapit o `backendOrigin` nalezal do efektu logo klienta i zostal odciety od swojego kodu
  // wstawieniem tego efektu — wrocil na swoje miejsce. Drugi twierdzil, ze „nie ma czego
  // odpytywac cyklicznie", co przeczy efektowi ponawiajacemu co 13 s dodanemu ponizej w TYM
  // SAMYM MR-ze; w pliku, w ktorym komentarz jest podstawowym zapisem decyzji, taki rozjazd
  // zaprasza kolejna osobe do usuniecia ponowienia jako „niezgodnego z opisem".
  //
  // Stan faktyczny: TEN efekt liczy werdykt raz, przy montowaniu. Ponowienia obsluguje osobny
  // efekt nizej (wylacznie w stanie AWARIA), a rotacje tokenu w trakcie zmiany — `wyslijNaStanowisko`,
  // ktore na 403 przelacza ekran na odmowe.
  useEffect(() => {
    let aktualne = true;
    sprawdzDostep().then((wynik) => {
      if (aktualne) setDostep(wynik);
    });

    return () => {
      aktualne = false;
    };
  }, []);

  // 🟠 `[access-verdict-frozen-after-mount]` (kontrola MR !34) — czesc pierwsza: POWROT PO AWARII.
  // Werdykt liczyl sie RAZ, przy montowaniu, wiec ekran „brak lacznosci" zostawal na kiosku do
  // konca zmiany, nawet gdy backend wrocil po minucie. Kiosk w sekretariacie stoi bez opieki i
  // nikt go nie przeladowuje — ekran musi wstac sam. Ponawiamy WYLACZNIE w stanie AWARIA: przy
  // OK nie ma czego sprawdzac, a przy ODMOWIE i tak nic sie nie zmieni bez zmiany adresu.
  //
  // 🟠 `[ponowienie-bez-oczekiwania]` (kontrola MR !34): to jest PETLA `setTimeout`, nie
  // `setInterval`. Interwal startowal nowe zadanie co 13 s NIE czekajac na poprzednie — przy
  // backendzie, ktory wisi zamiast odrzucac, zadania kumulowaly sie bez ograniczenia, a
  // przegladarka przepuszcza 6 polaczen na host, wiec NAJNOWSZE zapytanie (jedyne, ktorego
  // wynik przyjmujemy) ladowalo na koncu kolejki za zawieszonymi poprzednikami i ekran „brak
  // lacznosci" wisial dlugo po tym, jak backend wstal. Kolejne sprawdzenie planujemy dopiero
  // PO rozliczeniu poprzedniego; samo zapytanie ma wlasny timeout (`stationAccess.js`).
  useEffect(() => {
    if (dostep !== DOSTEP.AWARIA) return undefined;

    let anulowane = false;
    let timer = null;

    const zaplanuj = () => {
      timer = setTimeout(async () => {
        const wynik = await sprawdzDostep();
        if (anulowane) return;
        if (wynik === DOSTEP.AWARIA) {
          // Nadal awaria — planujemy kolejne podejscie, nie dotykajac stanu (zero zbednych
          // renderow i zero restartu tego efektu).
          zaplanuj();
          return;
        }
        setDostep(wynik);
      }, PONOWIENIE_PO_AWARII_MS);
    };

    zaplanuj();

    return () => {
      anulowane = true;
      if (timer) clearTimeout(timer);
    };
  }, [dostep]);

  // `backendOrigin` wyprowadzony DOKLADNIE tak, jak robi to juz src/index.js dla tla zakladu —
  // z `REACT_APP_API_BASE_URL` (`new URL(...).origin`, fallback `window.location.origin`), zeby
  // NIE wprowadzac drugiego zrodla konfiguracji. Probujemy zaladowac obraz PRZED pokazaniem go —
  // `Image().onerror` (instalacja bez logo klienta / backend 404) zostawia stan `null`, wiec
  // <img> nigdy sie nie montuje z niepoprawnym `src` (zero ikony zepsutego obrazka).
  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_BASE_URL || '';
    let backendOrigin = '';
    try {
      backendOrigin = new URL(apiUrl).origin;
    } catch {
      backendOrigin = window.location.origin;
    }

    const logoUrl = `${backendOrigin}/logo`;
    let cancelled = false;
    const probe = new Image();
    probe.onload = () => {
      if (!cancelled) setClientLogoUrl(logoUrl);
    };
    probe.onerror = () => {
      if (!cancelled) setClientLogoUrl(null);
    };
    probe.src = logoUrl;

    return () => {
      cancelled = true;
    };
  }, []);

  // Tryb kiosku: napis na przycisku "Rozpocznij szkolenie" ma isc w JEZYKU GRUPY (pierwsza
  // zeskanowana osoba), niezaleznie od jezyka, w ktorym renderuje sie reszta ekranu stanowiska.
  const groupLanguage = participants[0]?.language || null;
  const tGroup = groupLanguage ? i18next.getFixedT(groupLanguage) : t;
  // [i18n.jezyk-grupy-tylko-na-guziku] Cala tresc MATERIALU (VideoContent/PDFCourseContent, ktore
  // czytaja jezyk przez wlasny `withTranslation()`/kontekst providera) ma mowic w jezyku GRUPY, nie
  // instancji kiosku — dotad tylko przycisk startu to dostawal. `cloneInstance` dzieli zaladowane
  // juz zasoby (pl/en/ru/uk sa wczytane synchronicznie w src/i18n.js), wiec dziala od razu, bez
  // asynchronicznego ladowania. Memoizacja po `groupLanguage`, zeby nie tworzyc nowej instancji
  // (i nie remontowac odtwarzacza) przy KAZDYM renderze.
  const groupI18n = useMemo(
    () => (groupLanguage ? i18next.cloneInstance({ lng: groupLanguage }) : i18next),
    [groupLanguage],
  );

  // 🟠 `[access-verdict-frozen-after-mount]` (kontrola MR !34) — czesc druga: ROTACJA W TRAKCIE
  // PRACY. Komentarz przy tym efekcie obiecywal, ze rotacja tokenu uniewaznia ekran „przy
  // najblizszym zadaniu" — i to byla NIEPRAWDA: endpointy owszem odrzucaly stary token (403),
  // ale komponent zostawal na ekranie stanowiska, wiec operator skanowal kolejne osoby i dostawal
  // ciszę. Kazde 403 ze stanowiska przelacza teraz ekran na odmowe, czyli na komunikat mowiacy
  // wprost, ze adres jest nieaktualny i skad wziac nowy. Blad LECI DALEJ (`throw`), zeby
  // dotychczasowa obsluga bledow przy skanie/zaliczeniu dzialala bez zmian.
  const wyslijNaStanowisko = (path, body) =>
    postStation(path, body).catch((e) => {
      if (e?.response?.status === 403) setDostep(DOSTEP.ODMOWA);
      throw e;
    });

  const scheduleReturn = (ms, fn) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(fn, ms);
  };

  /**
   * PELNY EKRAN NA STANOWISKU (Marcin, 2026-09-23): „jak rozpoczynamy wideo, to przechodzimy
   * w tryb pelnoekranowy". Wolane z obslugi KLIKNIECIA „Rozpocznij szkolenie" — przegladarka
   * wpuszcza w pelny ekran wylacznie w obrebie gestu uzytkownika, a material rusza dopiero po
   * odpowiedzi serwera, wiec zadanie zlozone pozniej zostaloby odrzucone.
   * Odmowa przegladarki nie moze zatrzymac szkolenia — stad ciche `catch`.
   */
  const wejdzWPelnyEkranStanowiska = () => {
    if (typeof document === 'undefined' || document.fullscreenElement) return;
    const korzen = document.documentElement;
    try {
      if (korzen && typeof korzen.requestFullscreen === 'function') {
        const obietnica = korzen.requestFullscreen();
        if (obietnica && typeof obietnica.catch === 'function') obietnica.catch(() => {});
      }
    } catch (blad) { /* brak pelnego ekranu nie moze wywrocic szkolenia */ }
  };

  const wyjdzZPelnegoEkranuStanowiska = () => {
    if (typeof document === 'undefined' || !document.fullscreenElement) return;
    try {
      if (typeof document.exitFullscreen === 'function') {
        const obietnica = document.exitFullscreen();
        if (obietnica && typeof obietnica.catch === 'function') obietnica.catch(() => {});
      }
    } catch (blad) { /* jw. */ }
  };

  const resetToIdle = () => {
    zaliczanieWTokuRef.current = false;
    if (timerRef.current) clearTimeout(timerRef.current);
    // Powrot na ekran spoczynku zdejmuje pelny ekran — stanowisko ma znowu wygladac jak
    // urzadzenie czekajace na czlowieka, a nie jak zawieszony odtwarzacz.
    wyjdzZPelnegoEkranuStanowiska();
    setState(STATION_STATE.IDLE);
    // Karta oswiadczenia nie moze przezyc powrotu na ekran bazowy (kolejka nie zawisnie).
    setStatement(null);
    setStatementBusy(false);
    setParticipants([]);
    setGroupTrainingId(null);
    setGreeting(null);
    cardQueueRef.current = [];
    shownQueueCardRef.current = null;
    setCompletedNames([]);
    setCourseMaterials([]);
    setMaterialIndex(0);
    setCourseShowSeekControls(undefined);
  };

  // Anuluj / 34 s bez akcji: zero wpisu (brama nic nie zapisala). Z powrotem do zbierania grupy,
  // jesli karta ja przerwala; inaczej ekran bazowy.
  // Kolejka oswiadczen: gdy stanowisko wraca na ekran bazowy albo do grupy, pokazujemy karte
  // pierwszej osoby z kolejki (FIFO).
  useEffect(() => {
    if (dostep !== DOSTEP.OK || statement) return;
    if (state !== STATION_STATE.IDLE && state !== STATION_STATE.GRUPA) return;
    const next = statementQueueRef.current.shift();
    if (!next) return;
    statementReturnRef.current = state === STATION_STATE.GRUPA ? STATION_STATE.GRUPA : STATION_STATE.IDLE;
    setStatement({
      text: next.text,
      code: next.code,
      name: next.name,
      // `joinGroup` liczymy z BIEZACEGO ekranu (jak przy zwyklym skanie): false tylko przy pelnej
      // grupie. Stare `false` z chwili, gdy ekran byl zajety, rozjechaloby UI z serwerem — UI
      // doda te osobe do grupy po oswiadczeniu, wiec serwer tez musi na to pozwolic.
      joinGroup: state === STATION_STATE.GRUPA && participants.length >= MAX_GROUP_SIZE ? false : undefined,
    });
    setStatementBusy(false);
    setStatementError(null);
    setState(STATION_STATE.OSWIADCZENIE);
    scheduleReturn(STATEMENT_TIMEOUT_MS, closeStatement);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, statement, dostep, participants.length]);

  const closeStatement = () => {
    setStatement(null);
    setStatementError(null);
    setStatementBusy(false);
    if (statementReturnRef.current === STATION_STATE.GRUPA && participantsRef.current.length > 0) {
      if (timerRef.current) clearTimeout(timerRef.current);
      setState(STATION_STATE.GRUPA);
    } else {
      resetToIdle();
    }
  };

  const confirmStatement = () => {
    const pending = statement;
    if (!pending || statementBusy) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    setStatementBusy(true);
    setStatementError(null);
    const body = { code: pending.code };
    if (pending.joinGroup !== undefined) body.joinGroup = pending.joinGroup;
    wyslijNaStanowisko('/oswiadczenie', body)
      .then((response) => {
        // Backend nadal prosi o oswiadczenie = zapis sie nie udal: karta zostaje z komunikatem.
        if (response?.data?.gateAction === 'statement_required') {
          setStatementBusy(false);
          setStatementError(t('health_statement_save_failed'));
          scheduleReturn(STATEMENT_TIMEOUT_MS, closeStatement);
          return;
        }
        const base = statementReturnRef.current;
        setStatement(null);
        setStatementBusy(false);
        setState(base);
        handleScanResult(response?.data || {}, pending, base);
      })
      .catch(() => {
        setStatementBusy(false);
        setStatementError(t('health_statement_save_failed'));
        scheduleReturn(STATEMENT_TIMEOUT_MS, closeStatement);
      });
  };

  // Karta wejscia/wyjscia/odmowy + auto-powrot (odmowa dluzej). Wolana ze skanu na ekranie
  // spoczynku ORAZ ze skanu w trakcie innej karty (podmiana).
  const showGreeting = (data) => {
    // Nowy skan przerywa kolejke kart po zaliczeniu. Niezapisane wejscia z nieobejrzanej karty
    // odmowy NIE moga przepasc cicho — informacja o tych osobach idzie na pasek. Dotyczy tez karty
    // odmowy wyswietlanej wlasnie teraz (playCardQueue juz ja zdjal z kolejki).
    const pominieteOdmowy = [shownQueueCardRef.current, ...cardQueueRef.current]
      .filter((c) => c && c.gateAction === 'denied');
    cardQueueRef.current = [];
    shownQueueCardRef.current = null;
    pominieteOdmowy.forEach((c) => {
      const names = c.group ? c.group.map((g) => g.name).join(', ') : c.fullName;
      onNotice(t('station_cards_skipped', { names }));
    });
    setLogoHidden(false);
    setPlantLogoBroken(false); // jednorazowy blad wczytania nie kasuje logo z kolejnych kart
    setGreeting({ ...buildGreeting(data), at: new Date() });
    const teraz = Date.now();
    if (stateRef.current !== STATION_STATE.POWITANIE) greetingWindowStartRef.current = teraz;
    const pozostaleOkno = greetingWindowStartRef.current + GREETING_MAX_WINDOW_MS - teraz;
    setState(STATION_STATE.POWITANIE);
    scheduleReturn(
      Math.max(0, Math.min(
        data.gateAction === 'denied' ? DENIED_AUTO_RETURN_MS : GREETING_AUTO_RETURN_MS,
        pozostaleOkno,
      )),
      resetToIdle,
    );
  };

  // K8b: kolejne karty po zaliczeniu grupowym (wejscie zbiorcze, odmowy). Kazda karta ma wlasny
  // czas (odmowa dluzej); skan w trakcie karty przerywa kolejke (`showGreeting`).
  const playCardQueue = () => {
    const card = cardQueueRef.current.shift();
    shownQueueCardRef.current = card || null;
    if (!card) {
      resetToIdle();
      return;
    }
    if (card.gateAction === 'denied') playDenyBeep(); else playScanBeep();
    setLogoHidden(false);
    setPlantLogoBroken(false);
    setGreeting({ ...card, at: new Date() });
    greetingWindowStartRef.current = Date.now();
    setState(STATION_STATE.POWITANIE);
    scheduleReturn(
      card.gateAction === 'denied' ? DENIED_AUTO_RETURN_MS : GREETING_AUTO_RETURN_MS,
      cardQueueRef.current.length > 0 ? playCardQueue : resetToIdle,
    );
  };

  /**
   * Wynik `/skan` ORAZ `/oswiadczenie` (ta sama odpowiedz, ta sama logika — kontrakt F1). `request`
   * niesie kod i `joinGroup` tego skanu, zeby po potwierdzeniu oswiadczenia powtorzyc DOKLADNIE
   * to samo zadanie; `stateOverride` pozwala obsluzyc odpowiedz `/oswiadczenie` tak, jakby ekran
   * byl juz w stanie sprzed karty.
   */
  const handleScanResult = (data, request, stateOverride) => {
    // Dwa rozne dzwieki: 880 Hz tylko gdy skan KOGOS wpuszcza, 220 Hz square gdy bramka
    // odmawia. `missing_trainings` to nie odmowa dla goscia — idzie na szkolenie (GRUPA).
    if (data.gateAction === 'denied' && !maBrakujaceSzkolenia(data)) {
      playDenyBeep();
    } else if (data.gateAction !== 'statement_required') {
      playScanBeep();
    }
    setStationError(null);

    const currentState = stateOverride || stateRef.current;

    // Brama NIE zapisala wejscia i NIE dolaczyla osoby do grupy (D3) — prosi o oswiadczenie.
    if (data.gateAction === 'statement_required') {
      const { fullName } = personNames(data);
      const textOswiadczenia = (data.statement?.text || '').trim();
      // Bez tresci nie ma czego potwierdzac: pusta karta dawalaby „kosmetyczny" zapis. Blad
      // konfiguracji ma byc WIDOCZNY (dzwiek odmowy + komunikat), a wejscie nadal nie zapisane.
      if (!textOswiadczenia) {
        playDenyBeep();
        setStationError(t('health_statement_save_failed'));
        return;
      }
      // Ekran zajety (szkolenie, karta innej osoby, powitanie/zaliczone z auto-powrotem): NIE
      // gubimy tej osoby po cichu — dzwiek odmowy, widoczna informacja i miejsce w kolejce FIFO;
      // jej karta pojawi sie, gdy stanowisko wroci na ekran bazowy albo do grupy.
      if (currentState !== STATION_STATE.IDLE && currentState !== STATION_STATE.GRUPA) {
        playDenyBeep();
        const juzWKolejce = statementQueueRef.current.some((e) => e.code === request.code)
          || statementRef.current?.code === request.code;
        if (!juzWKolejce) {
          statementQueueRef.current.push({
            code: request.code, text: textOswiadczenia, name: fullName,
          });
        }
        // Ta sama osoba, ktorej karta jest juz na ekranie (ponowny odczyt) — bez zbednego paska.
        if (statementRef.current?.code !== request.code) {
          onNotice(t('station_statement_queued', { name: fullName }));
        }
        return;
      }
      statementReturnRef.current = currentState === STATION_STATE.GRUPA ? STATION_STATE.GRUPA : STATION_STATE.IDLE;
      setStatement({ text: textOswiadczenia, code: request.code, joinGroup: request.joinGroup, name: fullName });
      setStatementBusy(false);
      setStatementError(null);
      setState(STATION_STATE.OSWIADCZENIE);
      scheduleReturn(STATEMENT_TIMEOUT_MS, closeStatement);
      return;
    }

    if (currentState === STATION_STATE.OSWIADCZENIE && maBrakujaceSzkolenia(data)) {
      // Wejscie zapisane, ale ta osoba NIE dolaczy do grupy (karta innej osoby na ekranie) — musi
      // to byc WIDAC [ux.cichy-limit-grupy], nie cicho polkniete.
      onNotice(t('station_scan_during_statement', { name: personNames(data).fullName }));
    }
    if (currentState === STATION_STATE.SZKOLENIE || currentState === STATION_STATE.OSWIADCZENIE) {
      // (OSWIADCZENIE: karta innej osoby jest na ekranie — skan tej tutaj nie zmienia go.)
      // Zero zmiany ekranu — piknięcie i zapis juz sie odbyly, `joinGroup: false` wyzej
      // pilnuje, zeby serwer NIE dopisal tej osoby do zaliczanej grupy.
      return;
    }

    if (currentState === STATION_STATE.GRUPA) {
      if (maBrakujaceSzkolenia(data)) {
        const alreadyInGroup = participants.some((p) => p.userId === data.userId);
        if (!alreadyInGroup && participants.length >= MAX_GROUP_SIZE) {
          // [ux.cichy-limit-grupy] Wejscie i piknięcie juz sie odbyly (wyzej) — sa prawdziwe,
          // odbicie na liste ewakuacyjna trafia zawsze. Ale ta osoba NIE wchodzi do grupy
          // szkoleniowej i NIE dostanie zaliczenia, a ekranu i tak nikt nie ogląda — musi to
          // byc WIDAC operatorowi, nie cicho polkniete.
          setStationError(t('station_group_full', { max: MAX_GROUP_SIZE }));
          return;
        }
        if (!alreadyInGroup) {
          setStationError(null);
          setParticipants((prev) => [...prev, {
            userId: data.userId,
            displayName: data.displayName,
            language: data.language,
            trainingId: data.trainingId,
          }]);
        }
      }
      // Osoba juz przeszkolona: sam zapis wejscia (juz wykonany) + piknięcie, bez zmiany listy.
      return;
    }

    if (currentState === STATION_STATE.IDLE) {
      if (maBrakujaceSzkolenia(data)) {
        setParticipants([{
          userId: data.userId,
          displayName: data.displayName,
          language: data.language,
          trainingId: data.trainingId,
        }]);
        setGroupTrainingId(data.trainingId || null);
        setState(STATION_STATE.GRUPA);
        return;
      }

      showGreeting(data);
      return;
    }

    // Nowy wynik in/out/denied w trakcie karty POWITANIE (kolejna osoba skanuje, zanim poprzednia
    // karta zniknie) PODMIENIA karte i restartuje odliczanie — inaczej dzwiek mowilby jedno, a ekran
    // pokazywal zielony ptaszek poprzedniej osoby. Osoba z brakujacym szkoleniem nie zmienia ekranu
    // (joinGroup: false jak dotad), inne wyniki (duplicate/suppressed/still_inside) tez nie.
    if (currentState === STATION_STATE.POWITANIE && ['in', 'out', 'denied'].includes(data.gateAction)) {
      if (maBrakujaceSzkolenia(data)) {
        // Wejscie zapisane, ale bez `joinGroup` (POWITANIE) — osoba NIE trafia do grupy, a karta
        // poprzedniej osoby zostaje: musi byc WIDAC, nie cicho polkniete (jak w OSWIADCZENIE).
        onNotice(t('station_scan_during_greeting', { name: personNames(data).fullName }));
        return;
      }
      showGreeting(data);
      return;
    }

    // `powitanie` / `zaliczone` — ekrany przejsciowe z auto-powrotem; skan w tym oknie nie
    // zmienia jeszcze wyswietlanego ekranu (wraca on i tak za chwile sam).
  };

  /**
   * Wejscie zawsze idzie na `/skan`, TAKZE w stanach `grupa` i `szkolenie` — zapis wejscia nie
   * moze zalezec od tego, czy w danym momencie ktos patrzy na ekran (decyzja wlasciciela nr 6).
   * To, co skan ROBI z ekranem, zalezy od aktualnego stanu — patrz przelaczenie ponizej.
   */
  const handleScan = (rawCode) => {
    // 🟠 `[access-gate-does-not-disable-scanner]` (kontrola MR !34). `useStationScannerListener`
    // to HOOK — dziala od zamontowania komponentu i NIE zalezy od tego, ktory ekran akurat
    // renderujemy. Bez tej bramki czytnik HID wysylal `/skan` takze w trakcie sprawdzania dostepu
    // i na ekranie odmowy: backend i tak odmawial (403), ale ekran milczal, a plakietka wygladala
    // na zeskanowana. Bramka stoi TU, bo `handleScan` jest jedynym wejsciem dla OBU drog skanu
    // (czytnik HID i awaryjna kamera) — pilnowanie samego listenera zostawiloby druga droge.
    if (dostep !== DOSTEP.OK) return;

    // Blokada PER KOD — patrz komentarz przy `requestsInFlightRef` wyzej.
    if (requestsInFlightRef.current.has(rawCode)) return;
    requestsInFlightRef.current.add(rawCode);

    // 🔴 KOREKTA (kontrola/wartownik, finding [serwerowa-grupa-rozjechana-ze-stanem-ui], commit
    // f5470ac8, DOMKNIETE w kolejnym przebiegu kontroli — poprzednia wersja wysylala
    // `joinGroup: false` WYLACZNIE w stanie SZKOLENIE, dziura zostala w POWITANIE/ZALICZONE):
    // grupe zaliczana przez `/zalicz-grupowo` trzyma SERWER (TTL, `/skan` dopisuje do niej
    // KAZDA nieprzeszkolona osobe, niezaleznie od tego, co akurat pokazuje ekran — patrz
    // komentarz o proxy stanowiska na gorze pliku). Zamiast wyliczac stany, w ktorych NIE wolno
    // dopisywac (latwo o dziure przy kolejnym nowym stanie), wskazujemy JEDYNE stany, w ktorych
    // dopisanie do grupy jest ZAMIERZONE: `IDLE` (pierwsza osoba zaklada grupe) i `GRUPA`
    // (kolejne osoby do niej dolaczaja). KAZDY inny stan (`POWITANIE`, `SZKOLENIE`, `ZALICZONE`
    // — ekrany przejsciowe/odtwarzacz, gdzie skan ma dac TYLKO piknięcie + cichy zapis wejscia)
    // dostaje `joinGroup: false`.
    //
    // 🔴 KOREKTA (kontrola/wartownik, finding [server-group-limit-bypass]): sprawdzenie limitu
    // (`MAX_GROUP_SIZE`) dzialo dotad WYLACZNIE po stronie ekranu, PO wykonaniu `/skan` — w
    // stanie GRUPA zadanie nie mialo `joinGroup: false`, wiec serwer i tak dopisywal osobe MIMO
    // przekroczonego limitu (ekran twierdzil jedno, serwer robil drugie). Limit jest wiec
    // sprawdzany TU, PRZED wyslaniem zadania: gdy jestesmy w GRUPA i `participants.length` jest
    // juz na/nad `MAX_GROUP_SIZE`, wymuszamy `joinGroup: false` NIEZALEZNIE od tego, kim okaze
    // sie zeskanowana osoba (serwer i tak odsyla `missingTrainingsCount`/`displayName` — samo
    // "wejscie mimo pelnej grupy" wciaz dziala, po prostu nie ROZSZERZA grupy zaliczanej).
    // Osoba JUZ obecna w grupie (ponowny odczyt tej samej plakietki) tez dostanie `joinGroup:
    // false` przy tej regule — nieszkodliwe, bo nie musi byc dopisana drugi raz.
    const scanState = stateRef.current;
    const przyLimicieGrupy = scanState === STATION_STATE.GRUPA && participants.length >= MAX_GROUP_SIZE;
    const dolaczanieDoGrupyZamierzone = (scanState === STATION_STATE.IDLE || scanState === STATION_STATE.GRUPA) && !przyLimicieGrupy;
    const requestBody = { code: rawCode };
    if (!dolaczanieDoGrupyZamierzone) {
      requestBody.joinGroup = false;
    }

    wyslijNaStanowisko('/skan', requestBody)
      .then((response) => handleScanResult(response?.data || {}, { code: rawCode, joinGroup: requestBody.joinGroup }))
      .catch((error) => {
        // 🔴 KOREKTA (kontrola/wartownik, finding [cicha-awaria-skanu], commit f5470ac8): catch
        // traktowal IDENTYCZNIE "kod nierozpoznany" (backend swiadomie odrzuca 400/404 —
        // NIEZNANY/odwolany kod, CELOWA cisza: nie budujemy oracle'a mowiacego komukolwiek, czy
        // dana plakietka jest wazna) i KAZDA inna awarie (timeout, brak polaczenia, 500/502) —
        // przy tej drugiej wejscie MOGLO nie zostac zapisane, a ekran milczal, zero sladu dla
        // operatora. Rozroznienie: status 400/404 = backend ODPOWIEDZIAL i rozstrzygnal, ze kod
        // jest nieznany — cisza zostaje. WSZYSTKO INNE (brak `response` = network error/timeout,
        // albo status spoza 400/404, np. 500/502/503) = system NIE POTWIERDZIL zapisu — to MUSI
        // byc widoczne, tak jak juz jest dla innych cichych awarii w tym pliku (`station_group_
        // full`, `station_completion_failed`, `station_abort_sync_failed`).
        const status = error?.response?.status;
        if (status === 400 || status === 404) return;
        setStationError(t('station_scan_infra_error'));
      })
      .finally(() => {
        requestsInFlightRef.current.delete(rawCode);
      });
  };

  // Nasluch klawiaturowy czytnika HID (Magellan) — dziala NIEZALEZNIE od aktualnego ekranu
  // (idle/powitanie/grupa/szkolenie/zaliczone), patrz useStationScannerListener.js. Zero kamery,
  // zero przycisku uruchamiajacego skanowanie — czytnik "wpisuje" kod i konczy Enterem, system
  // wychwytuje go od razu (polecenie wlasciciela, dyktowanie 2026-09-17).
  useStationScannerListener(handleScan);

  /**
   * Materiał grupy = szkolenie PIERWSZEJ zeskanowanej osoby (tak jak jezyk grupy, decyzja
   * wlasciciela nr 3/6 z planu). 🔴 SWIADOMA DECYZJA WLASCICIELA: jesli w grupie sa osoby z
   * ROZNYMI brakujacymi szkoleniami, i tak odtwarzamy TYLKO material pierwszej osoby, a WSZYSCY
   * uczestnicy dostaja zaliczenie WLASNIE TEGO odtworzonego materialu (patrz `handleTrainingEnded`
   * ponizej — `userIds` to cala grupa, `trainingId` to `groupTrainingId` ustalony tu, przy
   * pierwszej osobie). Fizyczny nadzor (token stanowiska) zastepuje tu ankiete — to on jest
   * autoryzacja zaliczenia, nie dopasowanie kazdej osoby do jej WLASNEGO brakujacego szkolenia.
   * NIE „naprawiac" tego pozniej rozbiciem na osobne odtworzenia per osoba — to zmiana decyzji
   * wlasciciela, nie bug.
   *
   * Materiał pobieramy DOKLADNIE tak, jak robi to Training.js (`GET {apiUrl}{trainingId}`,
   * `getMaterialsFromCourseData` z `src/trainingMaterials.js`) i odtwarzamy TYM SAMYM
   * komponentem (`getContentHandler` -> VideoContent/PDFCourseContent, ForceLandscape, panel
   * przewijania sterowany `training:show_seek_controls`) — zero drugiej kopii odtwarzacza.
   */
  const handleStartTraining = () => {
    if (participants.length === 0) return;

    // W OBREBIE GESTU — patrz `wejdzWPelnyEkranStanowiska`. Przed walidacjami ponizej, bo one
    // moga skonczyc sie powrotem do spoczynku, ktory pelny ekran i tak zdejmie.
    wejdzWPelnyEkranStanowiska();

    if (!groupTrainingId) {
      // Brak trainingId w odpowiedzi skanu (np. backend jeszcze nie zna brakujacego szkolenia
      // tej osoby) — NIE wchodzimy w cichą zaslepke bez materialu. Czytelny komunikat +
      // powrot do ekranu bazowego, zamiast pustego/martwego odtwarzacza.
      setStationError(t('station_training_unavailable'));
      resetToIdle();
      return;
    }

    setMaterialIndex(0);
    setCourseMaterials([]);
    setStationError(null);
    setState(STATION_STATE.SZKOLENIE);

    // 🔴 KOREKTA (kontrola/wartownik, finding [server-group-not-cleared-after-material-failure]):
    // w chwili, gdy ten kod sie wykonuje, uczestnicy SA JUZ w serwerowej grupie TTL (kazdy ich
    // `/skan` w stanach idle/grupa ich tam dopisal — patrz komentarz przy `handleScan`). Brak
    // materialow albo blad `GET` wczesniej obslugiwal WYLACZNIE ekran (`resetToIdle()`) — grupa
    // na serwerze ZOSTAWALA, porzuceni uczestnicy wisieli do wygasniecia TTL i MOGLI zostac
    // zaliczeni w NASTEPNEJ, niepowiazanej sesji zebranej w tym samym oknie. Czyscimy grupe
    // TA SAMA DROGA, ktora czysci ja przycisk "Przerwij" (`handleAbort` nizej) — `/przerwij`
    // z `trainingId`. Best-effort/cicho: widocznym komunikatem operatora zostaje juz
    // `station_training_unavailable` (nadpisywanie go osobnym bledem synchronizacji byloby
    // mylace), a jesli samo to wywolanie padnie, TTL i tak posprzata grupe po 34 minutach —
    // dokladnie ta sama filozofia co w `handleAbort`.
    const porzucGrupeNaSerwerze = () => {
      wyslijNaStanowisko('/przerwij', { trainingId: groupTrainingId }).catch(() => {});
    };

    const apiUrl = process.env.REACT_APP_API_BASE_URL || '';
    axios.get(`${apiUrl}${groupTrainingId}`)
      .then((response) => {
        const courseData = response?.data?.training;
        const materials = getMaterialsFromCourseData(courseData);
        if (materials.length === 0) {
          setStationError(t('station_training_unavailable'));
          porzucGrupeNaSerwerze();
          resetToIdle();
          return;
        }
        setCourseMaterials(materials);
        setCourseShowSeekControls(courseData?.showSeekControls);
      })
      .catch(() => {
        setStationError(t('station_training_unavailable'));
        porzucGrupeNaSerwerze();
        resetToIdle();
      });
  };

  /**
   * [deploy/architektura, Marcin 2026-09-17]: grupe trzyma SERWER (TTL 34 min, `/skan` dopisuje
   * osoby), bo token stanowiska przestal byc sekretem (nginz kontenera doklada go do KAZDEGO
   * zadania na te sciezke) — klient nie moze juz byc zrodlem prawdy o `userIds`. „Przerwij" musi
   * wiec kasowac grupe TAKZE po stronie serwera, nie tylko resetowac ekran. Endpoint moze jeszcze
   * nie istniec/moze paść — TTL i tak zamknie grupe po 34 minutach, wiec awaria NIE jest
   * katastrofa, ale operator MUSI ja zobaczyc (dokladnie ten grzech, ktory naprawiamy gdzie
   * indziej w tym pliku: cicha zaslepka tam, gdzie czlowiek musi wiedziec).
   */
  const handleAbort = () => {
    const trainingIdToAbort = groupTrainingId;
    setStationError(null);
    if (trainingIdToAbort) {
      wyslijNaStanowisko('/przerwij', { trainingId: trainingIdToAbort }).catch(() => {
        setStationError(t('station_abort_sync_failed'));
      });
    }
    resetToIdle();
  };

  /**
   * Koniec materialu = zdarzenie `onEnded` odtwarzacza, NIGDY prog procentowy. VideoContent
   * (ten sam komponent co w trybie logowania) juz to gwarantuje — `handleEnded` tam ustawia
   * `isVideoFinished` NIEZALEZNIE od procentu (usterka reaktora !32: przy krotkim materiale
   * `played` nigdy nie dobija do 0.99). Guzik konczacy ostatni material ("Rozpocznij egzamin" —
   * etykieta z Training.js, tu bez realnego egzaminu) woła ten sam handler co poprzednio.
   */
  const handleTrainingEnded = () => {
    if (participants.length === 0) return;
    // E2E WK 08.10 (werken169, konto 714): auto-„Zakończ za N s" i drugie wyzwolenie wysłały DWA zaliczenia —
    // pierwsze zaliczyło grupę i ją wyczyściło, drugie dostało 400 `empty_participants`, a jego `catch` pokazał
    // „Grupa NIE została zaliczona”, choć wynik i wejście były zapisane. Jedno zaliczenie naraz.
    if (zaliczanieWTokuRef.current) return;
    zaliczanieWTokuRef.current = true;

    // [deploy/architektura, Marcin 2026-09-17] Grupe trzyma SERWER (TTL 34 min, `/skan` dopisuje
    // osoby) — `userIds` z ciala jest przez backend IGNOROWANY, nie odrzucany, wlasnie po to, zeby
    // kolejnosc wdrozenia frontu/backendu nie otwierala okna, w ktorym zaliczenie przepada. Lista
    // `participants` zostaje w stanie komponentu WYLACZNIE do wyswietlenia nazwisk na ekranie
    // "zaliczone" (`completedNames` ponizej) — nie jest juz zrodlem prawdy zaliczenia.
    setStationError(null);
    wyslijNaStanowisko('/zalicz-grupowo', { trainingId: groupTrainingId })
      .then((response) => {
        const cards = zaliczenieCards(response?.data?.completed);
        cardQueueRef.current = cards;
        setCompletedNames(participants.map((p) => p.displayName));
        setState(STATION_STATE.ZALICZONE);
        if (cards.length > 0) {
          scheduleReturn(COMPLETED_BEFORE_CARDS_MS, playCardQueue);
        } else {
          scheduleReturn(COMPLETED_AUTO_RETURN_MS, resetToIdle);
        }
      })
      .catch(() => {
        zaliczanieWTokuRef.current = false; // ponowienie po porażce musi być możliwe
        // [ux.cicha-porazka-zaliczenia / sec...-cicha-porazka, druga polowa] Zadne falszywe
        // potwierdzenie — ekran zostaje na odtwarzaczu, GRUPA NIE ZNIKA (participants/groupTrainingId
        // nietkniete, operator moze kliknac "Zakoncz szkolenie" ponownie), ale to MUSI byc widoczne —
        // wczesniej byla to cicha zaslepka, a pani z recepcji widziala dokladnie ten sam ekran co
        // przy sukcesie.
        setStationError(t('station_completion_failed'));
      });
  };

  // Materialow moze byc kilka (wideo + PDF, jak w Training.js) — przechodzimy przez nie po
  // kolei, a dopiero po OSTATNIM wolamy `handleTrainingEnded` (zaliczenie grupy).
  const handleMaterialContinue = () => {
    if (materialIndex < courseMaterials.length - 1) {
      setMaterialIndex(materialIndex + 1);
      return;
    }
    handleTrainingEnded();
  };

  // Znak zakladu klienta + znak LiveTraining (KOREKTA wlasciciela, runda 5, 2026-09-17: "logo
  // Werner Kenkel na gorze i NAJWIEKSZE, LiveTraining ponizej, nieco mniejsze, dopasowane
  // szerokoscia do logotypu klienta" — poprzednia wersja miala kolejnosc/proporcje odwrotne).
  // Oba obrazy dostaja TA SAMA szerokosc (`.lt-stanowisko-badge-logo`/`.lt-stanowisko-client-logo`
  // w index.css, `width` identyczny, `height: auto`) — przy wlasnej proporcji kazdego pliku
  // (LiveTraining 1584x672 ~2.36:1, szeroki poziomy baner; logo klienta z reguly bardziej
  // kwadratowe/pionowe, np. 124x100 ~1.24:1 na werken169) to WYSTARCZY, zeby logo klienta
  // wyszlo wizualnie WIEKSZE (wyzsze) przy tej samej szerokosci — "dopasowane szerokoscia", nie
  // dorownane wysokoscia. Gdy logo klienta NIE jest skonfigurowane (`clientLogoUrl === null`),
  // LiveTraining renderuje sie SAM, w swojej normalnej (nieco wiekszej) szerokosci — patrz reguly
  // `:only-child` w index.css — zero dziury po brakujacym logo.
  //
  // RUNDA 6, OSTATNIA KOREKTA NA TYM EKRANIE (Marcin, 2026-09-17): historia szerokosci bazowej
  // — 89px (przed runda 6) -> 267px (x3, cofniete) -> 187px (~210% z 89px) -> 303px (ten commit,
  // "jeden krok Fibonacciego"/+62% z 187px, wlasciciel: "wiecej zmian nie wdrazaj" — TEMAT
  // ZAMKNIETY). Baza w CSS (`.lt-stanowisko-badge-logo`) jest wiec 303px, skalowana JEDNYM
  // wymiarem (`width`, `height: auto`) — zero splaszczania. TWARDY gorny limit dolozony tutaj
  // przez `style={{ maxWidth: liveLogoMaxWidth }}`, zmierzony w `useLayoutEffect` wyzej z
  // realnego naglowka "Zeskanuj kod" (dziala niezaleznie od dlugosci tekstu w danym jezyku i od
  // rozdzielczosci) ZOSTAJE jako zabezpieczenie — przy 303px NADAL nie jest wiazacy na PL/EN
  // (naglowek jest szerszy, zapas jednak wyraznie mniejszy niz przy 187px — zmierzone w
  // dev/rejestracja-etykieta-gate.mjs), ale chroni przed dluzszymi tlumaczeniami (np.
  // niemiecki/finski), gdzie naglowek moze wyjsc krotszy niz 303px. Dopoki pomiar jeszcze nie
  // nadszedl (pierwsza klatka przed layoutem), CSS `max-width: 34vw` w index.css dziala jako
  // tymczasowy fallback, zeby logo nie mignelo w pelnym (303px) rozmiarze.
  const renderHeader = () => (
    <div className="lt-stanowisko-badge-header">
      {clientLogoUrl && (
        <img src={clientLogoUrl} alt="" className="lt-stanowisko-client-logo" />
      )}
      <img
        src={liveTrainingLogo}
        alt="LiveTraining"
        className="lt-stanowisko-badge-logo"
        style={liveLogoMaxWidth !== null ? { maxWidth: `${liveLogoMaxWidth}px` } : undefined}
      />
    </div>
  );

  // RUNDA 6 (Marcin, 2026-09-17): "domyslna ikonka czlowieka i imie, nazwisko jedno pod drugim,
  // dla jednej osoby jeden kafelek". Backend daje `displayName` jako JEDEN string ("Jan
  // Kowalski"), nie osobne pola imie/nazwisko — dzielimy go na PIERWSZY token (imie) i RESZTE
  // (nazwisko, takze wieloczlonowe: "Anna Maria Kowalska" -> "Anna" / "Maria Kowalska") tylko po
  // to, zeby zmiescic je w dwoch liniach pod ikonka, tak jak zazadal wlasciciel. To PRZYBLIZENIE
  // (backend nie rozdziela pol), udokumentowane tutaj zamiast ukryte.
  //
  // Renderowane jako JEDEN tekstowy wezel z prawdziwym znakiem nowej linii ("Jan\nKowalski"),
  // nie dwa osobne elementy — `.lt-stanowisko-person-name{white-space:pre-line}` w index.css
  // rysuje to na dwoch liniach wizualnie, ale dla `getByText`/czytnikow ekranu to nadal JEDEN,
  // ciagly napis "Jan Kowalski" (biblioteka testowa normalizuje biale znaki, w tym `\n`, do
  // pojedynczej spacji) — zero zmiany w sposobie, w jaki reszta kodu/testow szuka imienia i
  // nazwiska osoby.
  const splitDisplayName = (name) => {
    const [pierwsze, ...reszta] = (name || '').trim().split(/\s+/);
    return { pierwsze: pierwsze || '', drugie: reszta.join(' ') };
  };

  // RUNDA 6: kafelki zamiast listy "jeden pod drugim" — wlasciciel: "podzial parami, jedna obok
  // drugiej, trzecia osoba schodzi do drugiego wiersza" (siatka 2 kolumny, `.lt-stanowisko-
  // people-grid` w index.css) i "przy dziesiatej osobie modal nie moze wychodzic poza ekran"
  // (zmierzone `dev/rejestracja-etykieta-gate.mjs` dla 10 kafelkow, patrz komentarz przy
  // `MAX_GROUP_SIZE`). Wspolna dla ekranu "grupa" (uczestnicy w trakcie zbierania) i "zaliczone"
  // (te same osoby po zaliczeniu) — zero drugiej kopii ukladu kafelkow.
  const renderPersonTiles = (people) => (
    <div className="lt-stanowisko-people-grid" data-testid="station-people-grid">
      {people.map(({ key, name, denial }) => {
        const { pierwsze, drugie } = splitDisplayName(name);
        return (
          <div key={key} className="lt-stanowisko-person-tile">
            <MdPerson aria-hidden="true" className="lt-stanowisko-person-icon" />
            <span className="lt-stanowisko-person-name">
              {drugie ? `${pierwsze}\n${drugie}` : pierwsze}
            </span>
            {denial && (
              <span className="lt-stanowisko-person-denial" data-testid="station-person-denial">
                {deniedText(t, denial, i18n?.language)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );

  /**
   * Kamera jako DRUGA awaryjna droga skanowania (polecenie wlasciciela, 2026-09-17 — patrz
   * komentarz przy `cameraOpen` wyzej). Wymagania wlasciciela dosownie:
   *  - "niech ten przycisk bedzie zlany z tlem (...) niska sugestywnosc, mala subtelna ikonka" —
   *    `.lt-stanowisko-camera-fallback-btn` (index.css): brak wypelnienia/obramowania, niska
   *    nieprzezroczystosc, maly rozmiar, `position: fixed` w rogu EKRANU (nie kontenera) — dziala
   *    identycznie niezaleznie od tego, ktory stan akurat jest wyswietlany.
   *  - "logika dziala tak samo jak przy skanowaniu klawiatura" — `onDecoded` woła DOKLADNIE
   *    `handleScan` (ta sama blokada dubletow PER KOD `requestsInFlightRef`, te same stany, te
   *    same komunikaty `gateAction`) — ZERO drugiej sciezki obslugi wyniku skanu.
   *  - "wykorzystaj istniejacy QrScannerModal/useQrScanner" — wariant domyslny ("dialog"), TEN
   *    SAM komponent co w trybie logowania pracownika (App.js). Kamera montuje sie/startuje
   *    WYLACZNIE gdy `show` (czyli `cameraOpen`) jest `true` — zero automatycznego pytania o
   *    dostep do kamery, dopoki nikt nie kliknie przycisku.
   *  - Zamkniecie (X/„Anuluj") ustawia `cameraOpen=false` -> `QrScannerModal` sam zatrzymuje
   *    strumien w swoim efekcie zaleznym od `show` (`stop()`), zgodnie z jego istniejacym
   *    zachowaniem — nic tu nie trzeba dopisywac.
   *
   * 🟢 ZNANE OGRANICZENIE Z RUNDY 4 — ROZWIAZANE W RUNDZIE 5 (do raportu, zweryfikowane, nie
   * zalozone): poprzednia wersja opisywala tu ryzyko podwojnego zapisu, bo `QrScannerModal` w
   * wariancie "dialog" autofokusowal WLASNE pole recznego wpisania kodu na urzadzeniach bez
   * ekranu dotykowego, a to pole (bez `data-scan-ignore`) rownolegle zbieralo te same znaki co
   * globalny nasluch klawiaturowy stanowiska. Runda 5 usuwa TO POLE na stanowisku w ogole
   * (`hideManualInput` nizej) — `QrScannerModal.js` z `hideManualInput=true` w ogole NIE
   * renderuje `<Form data-track-id="qr-manual-form">`, wiec nie ma juz drugiego elementu, ktory
   * mogl przechwycic fokus/klawiature. Sciezka podwojnego zapisu jest wiec ZAMKNIETA, nie
   * zlagodzona — potwierdzone czytaniem `QrScannerModal.js` (blok `{!hideManualInput && (...)}`)
   * i testem w `RegistrationStation.cameraFallback.test.js` sprawdzajacym brak pola w DOM po
   * otwarciu kamery na stanowisku. Zachowanie App.js (bez tego propa) jest nietkniete — pole tam
   * zostaje dokladnie jak dzis.
   */
  const renderCameraFallback = () => (
    <>
      <button
        type="button"
        className="lt-stanowisko-camera-fallback-btn"
        aria-label={t('station_camera_fallback_label')}
        title={t('station_camera_fallback_label')}
        data-testid="station-camera-fallback-button"
        onClick={() => setCameraOpen(true)}
      >
        <MdPhotoCamera aria-hidden="true" />
      </button>
      <QrScannerModal
        show={cameraOpen}
        onDecoded={(rawCode) => {
          setCameraOpen(false);
          handleScan(rawCode);
        }}
        onClose={() => setCameraOpen(false)}
        hideManualInput
      />
    </>
  );

  // 🔒 BRAMKA DOSTEPU — PRZED kazdym innym ekranem (Marcin, 2026-09-23). Dopoki nie wiemy,
  // czy adres niesie wazny token, panel nie moze pokazac ani skanera, ani kamery: ekran
  // stanowiska wyglada identycznie jak dzialajacy, wiec operator zaczalby skanowac ludzi
  // w panel, ktory i tak nic nie zapisze.
  if (dostep === null) {
    return (
      <StationGlassScreen testId="station-access-checking">
        <div className="spinner-border" role="status" aria-hidden="true"></div>
      </StationGlassScreen>
    );
  }

  // Odmowa mowi operatorowi DOKLADNIE to, co sie stalo i co z tym zrobic (adres jest nieaktualny,
  // nowy bierze sie z ustawien systemowych od administratora) — a NIE „blad" ani „brak dostepu"
  // bez wskazowki, bo przy takim komunikacie recepcja dzwoni, ze „system padl".
  if (dostep === DOSTEP.ODMOWA) {
    return (
      <StationGlassScreen testId="station-access-denied">
        {renderHeader()}
        <h4 data-testid="station-access-denied-title">{t('station_access_denied_title')}</h4>
        <p className="mb-0">{t('station_access_denied_hint')}</p>
      </StationGlassScreen>
    );
  }

  // Awaria laczności z backendem — osobny komunikat, bo naprawia ja KTO INNY niz zly link
  // (administrator systemu kontra zgloszenie awarii) i bo panel nie moze wygladac na sprawny.
  if (dostep === DOSTEP.AWARIA) {
    return (
      <StationGlassScreen testId="station-access-unavailable">
        {renderHeader()}
        <h4 data-testid="station-access-unavailable-title">{t('station_access_unavailable_title')}</h4>
        <p className="mb-0">{t('station_access_unavailable_hint')}</p>
      </StationGlassScreen>
    );
  }

  // [ux.okno-bez-skanera] `powitanie` i `zaliczone` MUSZA renderowac skaner tak samo jak kazdy inny
  // ekran — zapis wejscia (decyzja wlasciciela nr 6) nie moze zalezec od tego, ktory ekran akurat
  // wisi po 3 s auto-powrotu. Wczesniej te dwa ekrany odmontowywaly kamere/pole na kod, wiec skan w
  // tym oknie w ogole nie docieral do `/skan` — martwa galaz kodu, gubione wejscia w kolejce.
  if (state === STATION_STATE.POWITANIE && greeting) {
    // Odmowa ma wlasna, czerwona karte i komunikat Z POWODEM (`denialReason`) — nie wpada w
    // powitanie. Fallback `station_greeting_in` WYLACZNIE dla naprawde nieoczekiwanej wartosci
    // `gateAction` (backend slal dotad: in/out/duplicate/suppressed/still_inside/denied).
    // K8 (WK 2026-10-02): wejscie, wyjscie i odmowa to DUZA karta na srodku (czytelna z 2-3 m):
    // logo (firmy zewnetrznej, jesli jest; inaczej zakladu) -> tekst -> duza ikona -> status -> godzina.
    const isDenied = greeting.gateAction === 'denied';
    const isGate = isDenied || greeting.gateAction === 'in' || greeting.gateAction === 'out';
    if (isGate) {
      const companyLogo = greeting.companyLogoUrl && !logoHidden ? greeting.companyLogoUrl : null;
      const plantLogo = plantLogoBroken ? null : clientLogoUrl;
      const logoSrc = companyLogo || plantLogo;
      const time = formatGateTime(greeting.at, i18n?.language);
      let kind;
      let Icon;
      let title;
      let status = null;
      if (isDenied) {
        kind = 'denied';
        Icon = MdBlock;
        title = greeting.group ? t(DENIED_FALLBACK_KEY) : deniedText(t, greeting, i18n?.language);
      } else {
        kind = greeting.gateAction;
        Icon = kind === 'in' ? MdCheckCircle : MdMeetingRoom;
        title = greeting.group ? null : t(GREETING_KEY_BY_GATE_ACTION[kind], { name: greeting.fullName });
        status = t(GREETING_SUB_KEY_BY_GATE_ACTION[kind]);
      }
      return (
        <StationGlassScreen
          testId="station-greeting-screen"
          cardClassName={`lt-stanowisko-gate-card lt-stanowisko-gate-card--${kind}${greeting.group ? ' lt-stanowisko-gate-card--group' : ''}${isDenied ? ' lt-stanowisko-denied-card' : ''}`}
        >
          {logoSrc ? (
            <img
              src={logoSrc}
              alt=""
              className="lt-stanowisko-gate-logo"
              data-testid={companyLogo ? 'station-company-logo' : 'station-plant-logo'}
              onError={() => { if (companyLogo) setLogoHidden(true); else setPlantLogoBroken(true); }}
            />
          ) : renderHeader()}
          <div data-testid={isDenied ? 'station-denied-card' : 'station-gate-card'}>
            {greeting.group && isDenied && (
              <div data-testid="station-denied-names">{renderPersonTiles(greeting.group)}</div>
            )}
            {greeting.group && !isDenied ? (
              <div className="lt-stanowisko-gate-title" data-testid="station-greeting">
                {renderPersonTiles(greeting.group)}
              </div>
            ) : (
              <h4 className="lt-stanowisko-gate-title" data-testid="station-greeting">{title}</h4>
            )}
            <Icon aria-hidden="true" className="lt-stanowisko-gate-icon" data-testid="station-gate-icon" />
            {status && (
              <p className="lt-stanowisko-gate-status mb-0" data-testid="station-greeting-sub">{status}</p>
            )}
            {status && time && (
              <p className="lt-stanowisko-gate-time mb-0" data-testid="station-gate-time">{time}</p>
            )}
          </div>
          {renderCameraFallback()}
        </StationGlassScreen>
      );
    }
    const greetingKey = GREETING_KEY_BY_GATE_ACTION[greeting.gateAction] || 'station_greeting_in';
    return (
      <StationGlassScreen testId="station-greeting-screen">
        {renderHeader()}
        <h4 data-testid="station-greeting">{t(greetingKey, { name: greeting.fullName })}</h4>
        {renderCameraFallback()}
      </StationGlassScreen>
    );
  }

  if (state === STATION_STATE.OSWIADCZENIE && statement) {
    return (
      <StationGlassScreen testId="station-statement-screen">
        {renderHeader()}
        <HealthStatementCard
          personName={statement.name}
          text={statement.text}
          busy={statementBusy}
          error={statementError}
          onConfirm={confirmStatement}
          onCancel={closeStatement}
        />
        {renderCameraFallback()}
      </StationGlassScreen>
    );
  }

  if (state === STATION_STATE.ZALICZONE) {
    // Zgloszenie Dawida Blachowskiego (Marcin, 2026-09-17): "jedna osoba to nie szkolenie
    // grupowe" — dotyczy TAKZE ekranu po zaliczeniu, nie tylko zbierania grupy.
    const completedTitleKey = completedNames.length === 1 ? 'station_completed_title_one' : 'station_completed_title_other';
    return (
      <StationGlassScreen testId="station-completed-screen">
        {renderHeader()}
        <h4>{t(completedTitleKey)}</h4>
        {renderPersonTiles(completedNames.map((name) => ({ key: name, name })))}
        {renderCameraFallback()}
      </StationGlassScreen>
    );
  }

  // WYJATEK od `StationGlassScreen` (Marcin, 2026-09-17): "w stanie szkolenia odtwarzacz
  // zostaje jak jest — materialu wideo NIE pakuj w szklany kontener". Ten ekran to AKTYWNA
  // TRESC (odtwarzacz na pelnym ekranie), nie komunikat/informacja — pozostale reguly
  // centrowania/glassmorphizmu go nie dotycza.
  if (state === STATION_STATE.SZKOLENIE) {
    const material = courseMaterials[materialIndex];
    let ContentHandler = null;
    if (material) {
      try {
        ContentHandler = getContentHandler(material.url);
      } catch (e) {
        // Nieobslugiwany format materialu — patrz fallback ponizej, brak ContentHandler.
      }
    }

    return (
      /* 🔴 EKRAN SZKOLENIA BEZ NAGLOWKA Z LOGOTYPAMI (Marcin, 2026-09-23 — prezentacja Werner
         Kenkel): „na samej gorze logotypy (...) one pozycjonowaly wideo ponizej ekranu".
         Naglowek (`renderHeader`) jest wspolny dla WSZYSTKICH ekranow stanowiska i tam zostaje —
         ale ten jeden ekran ma do pokazania MATERIAL, a dwa logotypy nad odtwarzaczem zabieraly
         mu tyle wysokosci, ze pasek sterowania i tresc schodzily pod krawedz. Znak firmowy widac
         na kazdym innym ekranie: spoczynku, powitania, grupy i zaliczenia. */
      <div className="App container bg-transparent h-100 d-flex flex-column" data-testid="station-training-screen">
        {stationError && (
          <div className="alert alert-warning" data-testid="station-error">{stationError}</div>
        )}
        {/* [i18n.jezyk-grupy-tylko-na-guziku] Caly kontekst grupy (material + jego UI, nie tylko
            guzik startu) mowi w jezyku GRUPY — `groupI18n` powyzej. */}
        <I18nextProvider i18n={groupI18n}>
          <div className="flex-grow-1 lt-stanowisko-material">
            {ContentHandler ? (
              <ContentHandler
                // key wymusza swiezy montaz przy przejsciu do kolejnego materialu (jak w Training.js).
                key={materialIndex}
                content={material}
                onExamStart={handleMaterialContinue}
                isLastMaterial={materialIndex >= courseMaterials.length - 1}
                showSeekControls={courseShowSeekControls}
                // Zgloszenie Dawida Blachowskiego (Marcin, 2026-09-17): na stanowisku material
                // zastepuje ankiete, a nadzor jest fizyczny — przeskoczenie DO PRZODU
                // uniewazniloby oba zalozenia. Cofanie zostaje (VideoContent.js: `allowSeekForward`).
                allowSeekForward={false}
                // Stanowisko NIE MA egzaminu z definicji trybu (nadzor zastepuje ankiete) — domyslna
                // etykieta odtwarzacza ("Rozpocznij egzamin") klamalaby gosciowi i pani z recepcji.
                // Uzywamy istniejacego klucza tlumaczenia (ta sama tresc, ktora Exam.js pokazuje w
                // modalu zakonczenia szkolenia bez testu), zamiast dokladac drugi o tym samym sensie.
                continueLabelKey="finish_training_title"
              />
            ) : (
              <div className="text-center py-5">{tGroup('loading_course_data')}</div>
            )}
          </div>
        </I18nextProvider>
        {renderCameraFallback()}
      </div>
    );
  }

  if (state === STATION_STATE.GRUPA) {
    return (
      <StationGlassScreen testId="station-group-screen">
        {renderHeader()}
        {stationError && (
          <div className="alert alert-warning" data-testid="station-error">{stationError}</div>
        )}
        {/* Zgloszenie Dawida Blachowskiego (Marcin, 2026-09-17): "jedna osoba to nie szkolenie
            grupowe" — przy jednym uczestniku ekran mowi o SZKOLENIU TEJ OSOBY (z imieniem i
            nazwiskiem), nie o "grupie oczekujacej". */}
        <h5>
          {participants.length === 1
            ? t('station_group_title_one', { name: participants[0].displayName })
            : t('station_group_title_other')}
        </h5>
        {/* RUNDA 6 (Marcin, 2026-09-17): kafelki w zawijanej siatce (2 kolumny) zamiast listy
            "jeden pod drugim" — zmierzone `dev/rejestracja-etykieta-gate.mjs` dla 10 kafelkow na
            obu rozdzielczosciach (karta rosnie/scrolluje sie sama, `.lt-stanowisko-glass-card`
            juz ma `overflow-y: auto` z rundy 3 — nic dodatkowego nie trzeba tu dokladac). */}
        {renderPersonTiles(participants.map((p) => ({ key: p.userId, name: p.displayName })))}
        {/* RUNDA 6 (Marcin, 2026-09-17 — wlasciciel WYCOFAL pierwsza wersje z dlugim tekstem NA
            przycisku "za dlugi napis, znowu problem"): osobna podpowiedz POD kafelkami, NAD
            przyciskiem — przycisk "Rozpocznij szkolenie" zostaje BEZ ZMIAN (tak jak dotad). */}
        <p className="lt-stanowisko-scan-hint" data-testid="station-scan-another-hint">
          {tGroup('station_scan_another_hint')}
        </p>
        <div className="d-grid gap-2">
          <Button variant="danger" onClick={handleStartTraining} data-track-id="station-start-training">
            {tGroup('station_start_training')}
          </Button>
          <Button variant="secondary" onClick={handleAbort} data-track-id="station-abort">
            {t('station_abort')}
          </Button>
        </div>
        {renderCameraFallback()}
      </StationGlassScreen>
    );
  }

  // Ekran spoczynku (Marcin, 2026-09-17): stanowisko stoi caly dzien na portierni i ma wygladac
  // jak urzadzenie CZEKAJACE na czlowieka — tlo zakladu (`--background-image-url` na `body`,
  // patrz src/index.js + src/index.css) widoczne przez PRZEZROCZYSTY kontener poza karta.
  //
  // 🔴 KOREKTA (Marcin, 2026-09-17): wczesniej ekran spoczynku trzymal zamontowany caly czas
  // `QrScannerModal` (wariant `embedded`, kamera + `useQrScanner`). Wlasciciel chce skaner
  // KLAWIATUROWY (czytnik HID podpiety jako klawiatura, zero kamery, zero przycisku) — patrz
  // `useStationScannerListener` uzyty wyzej w komponencie. Pole recznego wpisania kodu zostalo
  // usuniete w rundzie 5 (czlowiek i tak nie odczyta tresci kodu QR) — czytnik HID jest jedyna
  // droga, kamera (ponizej) to jedyny awaryjny fallback.
  //
  // 🔴 KOREKTA #2/#3 (Marcin, 2026-09-17): "logo LiveTraining na srodku z logiem Werner Kenkel
  // jeszcze" + "wszystko centrujemy do srodka ekranu" + "informacje i komunikaty powinny
  // wyskakiwac na srodku ekranu (...) w glasmorficznym kontenerku" — ten ekran uzywa TERAZ
  // wspolnego `StationGlassScreen` (jak kazdy inny stan poza "szkolenie"), zero wlasnej kopii
  // ukladu.
  return (
    <StationGlassScreen testId="station-idle-screen">
      {renderHeader()}
      {stationError && (
        <div className="alert alert-warning" data-testid="station-error">{stationError}</div>
      )}
      <h1 className="lt-stanowisko-idle-title" ref={idleTitleRef}>{t('station_scan_prompt')}</h1>
      <p className="lt-stanowisko-idle-hint" data-testid="station-idle-hint">
        {t('station_idle_waiting')}
      </p>
      {renderCameraFallback()}
    </StationGlassScreen>
  );
};

/**
 * Wrapper z PASKIEM INFORMACJI nad KAZDYM ekranem stanowiska (takze nad odtwarzaczem szkolenia i
 * kartami z auto-powrotem): nieblokujacy, znika sam po 13 s. Tu trafia komunikat o osobie, ktorej
 * oswiadczenie czeka w kolejce, bo ekran byl zajety.
 */
const NOTICE_MS = 13000;
const RegistrationStation = () => {
  const [notice, setNotice] = useState(null);
  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice]);

  return (
    <>
      {notice && (
        <div
          className="alert alert-warning text-center"
          role="status"
          data-testid="station-statement-notice"
          style={{ position: 'fixed', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 2000, maxWidth: '90vw' }}
        >
          {notice}
        </div>
      )}
      <RegistrationStationScreens onNotice={setNotice} />
    </>
  );
};

export default RegistrationStation;
